import { useState } from 'react';

interface Task {
  id: string;
  title: string;
  description: string;
  status: 'todo' | 'in_progress' | 'done';
  category: string;
  estimatedTime: string;
}

interface JobToBeDone {
  id: string;
  title: string;
  description: string;
  icon: string;
  tasks: Task[];
  frequency: string;
}

export default function WorkDashboard() {
  const [activeJob, setActiveJob] = useState<string | null>(null);

  const jobsToBeDone: JobToBeDone[] = [
    {
      id: 'client-onboarding',
      title: 'Client Onboarding',
      description: 'Set up new client from scratch to first review request',
      icon: '🎯',
      frequency: 'Per new client',
      tasks: [
        {
          id: 'initial-call',
          title: 'Discovery Call',
          description: 'Understand their business, customer flow, and pain points',
          status: 'todo',
          category: 'onboarding',
          estimatedTime: '30 min'
        },
        {
          id: 'audit-online-presence',
          title: 'Audit Current Reviews',
          description: 'Check Google, Facebook, Yelp - screenshot current state',
          status: 'todo',
          category: 'onboarding',
          estimatedTime: '15 min'
        },
        {
          id: 'claim-profiles',
          title: 'Claim/Verify Profiles',
          description: 'Ensure they own their Google Business, Facebook page, etc.',
          status: 'todo',
          category: 'onboarding',
          estimatedTime: '20 min'
        },
        {
          id: 'customer-list',
          title: 'Get Customer List',
          description: 'Export from POS, CRM, or create spreadsheet template',
          status: 'todo',
          category: 'onboarding',
          estimatedTime: '10 min'
        },
        {
          id: 'setup-automation',
          title: 'Configure Automation',
          description: 'Set up review request workflow with optimal timing',
          status: 'todo',
          category: 'onboarding',
          estimatedTime: '45 min'
        },
        {
          id: 'test-workflow',
          title: 'Test Review Flow',
          description: 'Send test review request to yourself, verify all links work',
          status: 'todo',
          category: 'onboarding',
          estimatedTime: '15 min'
        },
        {
          id: 'train-team',
          title: 'Train Their Team',
          description: 'Show them how to ask for reviews in-person, respond to feedback',
          status: 'todo',
          category: 'onboarding',
          estimatedTime: '30 min'
        }
      ]
    },
    {
      id: 'review-collection',
      title: 'Review Collection',
      description: 'Weekly review outreach and collection tasks',
      icon: '📧',
      frequency: 'Weekly',
      tasks: [
        {
          id: 'upload-customers',
          title: 'Upload New Customers',
          description: 'Get list of customers from past week, add to system',
          status: 'todo',
          category: 'collection',
          estimatedTime: '10 min'
        },
        {
          id: 'ai-timing',
          title: 'AI Optimal Timing Analysis',
          description: 'Let AI analyze best send times based on business type',
          status: 'todo',
          category: 'collection',
          estimatedTime: '5 min'
        },
        {
          id: 'personalize-messages',
          title: 'Personalize Messages',
          description: 'AI generates personalized messages, you review and approve',
          status: 'todo',
          category: 'collection',
          estimatedTime: '15 min'
        },
        {
          id: 'send-requests',
          title: 'Send Review Requests',
          description: 'Trigger SMS/email campaign to customers',
          status: 'todo',
          category: 'collection',
          estimatedTime: '5 min'
        },
        {
          id: 'monitor-responses',
          title: 'Monitor Responses',
          description: 'Check for incoming reviews and feedback',
          status: 'todo',
          category: 'collection',
          estimatedTime: '20 min'
        },
        {
          id: 'follow-up',
          title: 'Smart Follow-Up',
          description: 'Send gentle reminders to non-responders (AI-generated)',
          status: 'todo',
          category: 'collection',
          estimatedTime: '10 min'
        }
      ]
    },
    {
      id: 'review-moderation',
      title: 'Review Moderation',
      description: 'Handle incoming reviews and negative feedback',
      icon: '🛡️',
      frequency: 'Daily',
      tasks: [
        {
          id: 'check-new-reviews',
          title: 'Check New Reviews',
          description: 'Review all incoming feedback from past 24 hours',
          status: 'todo',
          category: 'moderation',
          estimatedTime: '15 min'
        },
        {
          id: 'sentiment-routing',
          title: 'Sentiment Classification',
          description: 'AI categorizes: positive → public, negative → private',
          status: 'todo',
          category: 'moderation',
          estimatedTime: '5 min'
        },
        {
          id: 'handle-negative',
          title: 'Handle Negative Feedback',
          description: 'Alert owner, help them respond privately, resolve issue',
          status: 'todo',
          category: 'moderation',
          estimatedTime: '30 min'
        },
        {
          id: 'approve-public',
          title: 'Approve Public Reviews',
          description: 'Review positive feedback, approve for posting',
          status: 'todo',
          category: 'moderation',
          estimatedTime: '10 min'
        },
        {
          id: 'draft-responses',
          title: 'Draft Responses',
          description: 'AI generates personalized responses, you refine',
          status: 'todo',
          category: 'moderation',
          estimatedTime: '20 min'
        },
        {
          id: 'post-to-platforms',
          title: 'Post to Platforms',
          description: 'Publish approved reviews to Google, Facebook, etc.',
          status: 'todo',
          category: 'moderation',
          estimatedTime: '15 min'
        }
      ]
    },
    {
      id: 'review-activation',
      title: 'Review Activation',
      description: 'Turn reviews into marketing content',
      icon: '🚀',
      frequency: 'Weekly',
      tasks: [
        {
          id: 'select-highlights',
          title: 'Select Review Highlights',
          description: 'Choose best reviews from past week for marketing',
          status: 'todo',
          category: 'activation',
          estimatedTime: '15 min'
        },
        {
          id: 'create-social-posts',
          title: 'Create Social Media Posts',
          description: 'AI generates Instagram, Facebook, LinkedIn posts from reviews',
          status: 'todo',
          category: 'activation',
          estimatedTime: '20 min'
        },
        {
          id: 'design-graphics',
          title: 'Design Graphics',
          description: 'Create quote cards, highlight reels for social',
          status: 'todo',
          category: 'activation',
          estimatedTime: '30 min'
        },
        {
          id: 'newsletter-content',
          title: 'Newsletter Content',
          description: 'Write newsletter section featuring customer stories',
          status: 'todo',
          category: 'activation',
          estimatedTime: '25 min'
        },
        {
          id: 'website-testimonials',
          title: 'Update Website Testimonials',
          description: 'Add new reviews to their testimonials page',
          status: 'todo',
          category: 'activation',
          estimatedTime: '15 min'
        },
        {
          id: 'email-signature',
          title: 'Update Email Signatures',
          description: 'Add latest rating/review count to team signatures',
          status: 'todo',
          category: 'activation',
          estimatedTime: '10 min'
        },
        {
          id: 'sales-materials',
          title: 'Update Sales Materials',
          description: 'Add testimonials to proposals, pitch decks',
          status: 'todo',
          category: 'activation',
          estimatedTime: '20 min'
        }
      ]
    },
    {
      id: 'monthly-reporting',
      title: 'Monthly Reporting',
      description: 'Show ROI and progress to client',
      icon: '📊',
      frequency: 'Monthly',
      tasks: [
        {
          id: 'gather-metrics',
          title: 'Gather Metrics',
          description: 'Reviews collected, response rate, sentiment breakdown',
          status: 'todo',
          category: 'reporting',
          estimatedTime: '15 min'
        },
        {
          id: 'compare-competitors',
          title: 'Competitor Analysis',
          description: 'Check competitor reviews, show how client is winning',
          status: 'todo',
          category: 'reporting',
          estimatedTime: '20 min'
        },
        {
          id: 'create-report',
          title: 'Create Visual Report',
          description: 'Design one-page report with key wins and metrics',
          status: 'todo',
          category: 'reporting',
          estimatedTime: '30 min'
        },
        {
          id: 'schedule-review-call',
          title: 'Schedule Review Call',
          description: 'Book 30-min call to present results and plan next month',
          status: 'todo',
          category: 'reporting',
          estimatedTime: '5 min'
        },
        {
          id: 'present-results',
          title: 'Present Results',
          description: 'Walk through wins, show testimonials activated, discuss improvements',
          status: 'todo',
          category: 'reporting',
          estimatedTime: '30 min'
        },
        {
          id: 'collect-feedback',
          title: 'Collect Client Feedback',
          description: 'Ask what\'s working, what they want more of',
          status: 'todo',
          category: 'reporting',
          estimatedTime: '10 min'
        }
      ]
    },
    {
      id: 'system-improvement',
      title: 'System Improvement',
      description: 'Build tools and automation to work smarter',
      icon: '⚙️',
      frequency: 'Ongoing',
      tasks: [
        {
          id: 'identify-bottleneck',
          title: 'Identify Bottleneck',
          description: 'What task takes longest or is most repetitive?',
          status: 'todo',
          category: 'improvement',
          estimatedTime: '10 min'
        },
        {
          id: 'document-process',
          title: 'Document Current Process',
          description: 'Write down exactly how you do it now',
          status: 'todo',
          category: 'improvement',
          estimatedTime: '20 min'
        },
        {
          id: 'design-automation',
          title: 'Design Automation',
          description: 'Plan how AI or workflows could handle this',
          status: 'todo',
          category: 'improvement',
          estimatedTime: '30 min'
        },
        {
          id: 'build-tool',
          title: 'Build Quick Tool',
          description: 'Create simple script or UI to automate task',
          status: 'todo',
          category: 'improvement',
          estimatedTime: '2 hours'
        },
        {
          id: 'test-with-client',
          title: 'Test with One Client',
          description: 'Validate automation works before rolling out',
          status: 'todo',
          category: 'improvement',
          estimatedTime: '30 min'
        },
        {
          id: 'document-tool',
          title: 'Document Tool Usage',
          description: 'Write instructions so VA or client could use it',
          status: 'todo',
          category: 'improvement',
          estimatedTime: '15 min'
        }
      ]
    }
  ];

  const categoryColors: Record<string, string> = {
    onboarding: 'bg-blue-100 text-blue-800',
    collection: 'bg-green-100 text-green-800',
    moderation: 'bg-yellow-100 text-yellow-800',
    activation: 'bg-purple-100 text-purple-800',
    reporting: 'bg-pink-100 text-pink-800',
    improvement: 'bg-gray-100 text-gray-800'
  };

  const toggleTask = (jobId: string, taskId: string) => {
    // In a real app, this would update state/backend
    console.log(`Toggle task ${taskId} in job ${jobId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 text-white py-8 px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold mb-2">Hello.Reviews Work Dashboard</h1>
          <p className="text-green-100">Your service delivery playbook - from zero to 100 happy clients</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="max-w-7xl mx-auto px-6 -mt-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-green-600">0</div>
            <div className="text-sm text-gray-600 mt-1">Active Clients</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-blue-600">0</div>
            <div className="text-sm text-gray-600 mt-1">Reviews Collected</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-purple-600">0</div>
            <div className="text-sm text-gray-600 mt-1">Content Pieces Created</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-pink-600">$0</div>
            <div className="text-sm text-gray-600 mt-1">MRR</div>
          </div>
        </div>
      </div>

      {/* Jobs Menu */}
      <div className="max-w-7xl mx-auto px-6 pb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Jobs To Be Done</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {jobsToBeDone.map((job) => (
            <div
              key={job.id}
              className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setActiveJob(activeJob === job.id ? null : job.id)}
            >
              <div className="p-6">
                <div className="text-4xl mb-4">{job.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{job.title}</h3>
                <p className="text-sm text-gray-600 mb-4">{job.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {job.frequency}
                  </span>
                  <span className="text-xs font-semibold text-green-600">
                    {job.tasks.length} tasks
                  </span>
                </div>
              </div>

              {/* Expanded Task List */}
              {activeJob === job.id && (
                <div className="border-t border-gray-200 p-6 bg-gray-50">
                  <h4 className="font-semibold text-gray-900 mb-4">Task Checklist:</h4>
                  <div className="space-y-3">
                    {job.tasks.map((task) => (
                      <div
                        key={task.id}
                        className="flex items-start space-x-3 p-3 bg-white rounded border border-gray-200 hover:border-green-300 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={task.status === 'done'}
                          onChange={() => toggleTask(job.id, task.id)}
                          className="mt-1 h-5 w-5 text-green-600 rounded focus:ring-green-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <h5 className="font-medium text-gray-900 text-sm">{task.title}</h5>
                            <span className={`text-xs px-2 py-1 rounded ${categoryColors[task.category]}`}>
                              {task.estimatedTime}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600">{task.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="max-w-7xl mx-auto px-6 pb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button className="bg-green-500 hover:bg-green-600 text-white font-semibold py-4 px-6 rounded-lg shadow transition-colors text-left">
            <div className="text-2xl mb-2">➕</div>
            <div className="font-bold">Add New Client</div>
            <div className="text-sm text-green-100">Start onboarding workflow</div>
          </button>
          <button className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-4 px-6 rounded-lg shadow transition-colors text-left">
            <div className="text-2xl mb-2">📧</div>
            <div className="font-bold">Send Review Requests</div>
            <div className="text-sm text-blue-100">Batch send this week</div>
          </button>
          <button className="bg-purple-500 hover:bg-purple-600 text-white font-semibold py-4 px-6 rounded-lg shadow transition-colors text-left">
            <div className="text-2xl mb-2">📊</div>
            <div className="font-bold">Generate Reports</div>
            <div className="text-sm text-purple-100">Monthly client reports</div>
          </button>
          <a
            href="#dropstuff"
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-4 px-6 rounded-lg shadow transition-colors text-left block"
          >
            <div className="text-2xl mb-2">📎</div>
            <div className="font-bold">Dropstuff</div>
            <div className="text-sm text-orange-100">Upload & analyze content</div>
          </a>
        </div>
      </div>
    </div>
  );
}
